/**
 * Presigned direct-to-R2 upload (node V2): the all-Cloudflare video upload. Given
 * the `provider:"r2-hls"` reserve response, the browser asks the Worker to sign the
 * parts it still needs, then PUTs each part **straight to R2** (one presigned PUT
 * per part) and reads the part's ETag off the response: no bytes proxy through the
 * Worker, so multi-GB files upload at R2 speed. Parts are PUT through a small pool
 * (`partsInFlight`, default 6) so a fast connection stays busy. A single
 * `complete` assembles them.
 * It is **resumable**: the caller hands back the parts that already succeeded
 * (`resumeFrom`) so an interrupted upload re-signs + re-sends only the missing parts.
 *
 * The part engine (`uploadParts`, `uploadPartsWithRecovery`) knows nothing of
 * webinars: the caller hands it the sign and complete calls. The webinar passes
 * its own here; a Room video passes the notes calls (src/lib/noteUpload.ts).
 *
 * Pure + fetch-injectable → unit-tested in test/video-upload.test.ts with no network.
 *
 * NOTE (deployment): the part PUT is cross-origin (app → R2), so the `flashroom-video`
 * bucket needs a CORS policy allowing `PUT` from the app origin AND exposing the
 * `ETag` response header: the browser must read each part's ETag to complete.
 */
import type {
  PresignedPart,
  UploadAbortRequest,
  R2UploadStartResponse,
  UploadCompleteResponse,
  UploadSignResponse,
  UploadPartResult,
} from "../../shared/types";
import { ApiError } from "./api";

interface ResumableUploadOptions {
  /** Swap the transport in tests. Defaults to the global `fetch`. */
  fetchImpl?: typeof fetch;
  /** Parts already uploaded (e.g. from a previous, interrupted attempt): skipped. */
  resumeFrom?: UploadPartResult[];
  /** Called after each part lands, so the caller can persist progress for resume. */
  onPartComplete?: (part: UploadPartResult, done: number, total: number) => void;
  /**
   * Called once EVERY part is acknowledged and before the assemble request goes
   * out. That request is the point of no return (it flips the webinar to
   * `probing` and queues the transcode), so this is where a caller stops treating
   * the multipart as its own to cancel.
   */
  onBeforeComplete?: () => void;
  /**
   * Abort the whole upload (user cancel, or the view unmounting). Honored by every
   * request the helper makes: signing, each part PUT, and the final complete.
   */
  signal?: AbortSignal;
  /** Retries per part before giving up (transient network). Default 5. */
  maxRetriesPerPart?: number;
  /** Swap the backoff sleep in tests. Defaults to a real, abort-aware wait. */
  delayImpl?: (ms: number) => Promise<void>;
  /**
   * How many part PUTs may be in flight at once. Default 6: one request at a
   * time pays its own round trip before the next byte moves, which on a 7.3 GB
   * recording (730 sequential PUTs) rarely fills a fast connection. Parts still
   * START in ascending order, so the queue is fair.
   */
  partsInFlight?: number;
}

/** An abort-aware sleep: the backoff between retries dies with the upload. */
function wait(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      signal?.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new ApiError(0, "Upload canceled."));
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/** The byte ranges each part covers, 1-indexed like S3/R2 part numbers. */
export function planParts(
  size: number,
  partSize: number,
): Array<{ partNumber: number; start: number; end: number }> {
  const total = Math.max(1, Math.ceil(size / partSize));
  const parts: Array<{ partNumber: number; start: number; end: number }> = [];
  for (let i = 0; i < total; i++) {
    parts.push({
      partNumber: i + 1,
      start: i * partSize,
      end: Math.min(size, (i + 1) * partSize),
    });
  }
  return parts;
}

/**
 * The two server calls that tie the part engine to one kind of upload: presign
 * some part numbers, and assemble the receipts once every part is in.
 */
export interface PartUploadCalls<T> {
  sign: (partNumbers: number[], signal?: AbortSignal) => Promise<PresignedPart[]>;
  complete: (parts: UploadPartResult[], signal?: AbortSignal) => Promise<T>;
}

/** The webinar recording's sign and complete calls. */
function webinarCalls(
  fetchImpl: typeof fetch,
  webinarId: string,
  start: R2UploadStartResponse,
): PartUploadCalls<UploadCompleteResponse> {
  return {
    async sign(partNumbers, signal) {
      const res = await fetchImpl(`/api/webinars/${webinarId}/video/upload/sign`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        signal,
        body: JSON.stringify({
          video_id: start.video_id,
          upload_id: start.upload_id,
          part_numbers: partNumbers,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiError(res.status, data?.error ?? "We couldn't prepare that upload.");
      }
      return ((await res.json()) as UploadSignResponse).urls;
    },
    async complete(parts, signal) {
      const res = await fetchImpl(`/api/webinars/${webinarId}/video/upload/complete`, {
        method: "POST",
        credentials: "same-origin",
        headers: { "content-type": "application/json" },
        signal,
        body: JSON.stringify({
          video_id: start.video_id,
          upload_id: start.upload_id,
          parts,
        }),
      });
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null;
        throw new ApiError(res.status, data?.error ?? "We couldn't finish that upload.");
      }
      return (await res.json()) as UploadCompleteResponse;
    },
  };
}

/** Ask the server to presign the given part numbers' UploadPart URLs. */
async function signParts<T>(
  calls: PartUploadCalls<T>,
  partNumbers: number[],
  signal?: AbortSignal,
): Promise<Map<number, string>> {
  // A canceled upload asks for nothing: signing is the first thing that would
  // otherwise keep running after the host walked away.
  if (signal?.aborted) throw new ApiError(0, "Upload canceled.");
  const urls = await calls.sign(partNumbers, signal);
  return new Map(urls.map((u) => [u.part_number, u.url]));
}

/**
 * PUT one part **directly to its presigned R2 URL**; returns the part's ETag
 * receipt. Built for slow, flaky connections: each retry backs off (2s, 4s, 8s…
 * capped at 30s, so a brief outage isn't burned through in a blink) and asks for
 * a FRESH presigned URL — a long upload outlives the 15-minute signature, and
 * retrying the stale URL would only ever earn a 403.
 */
async function putPartDirect(
  fetchImpl: typeof fetch,
  urlFor: (fresh: boolean) => Promise<string>,
  partNumber: number,
  body: Blob,
  maxRetries: number,
  delay: (ms: number) => Promise<void>,
  signal?: AbortSignal,
): Promise<UploadPartResult> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    if (signal?.aborted) throw new ApiError(0, "Upload canceled.");
    try {
      if (attempt > 0) await delay(Math.min(30_000, 1000 * 2 ** attempt));
      const url = await urlFor(attempt > 0);
      // Cross-origin PUT straight to R2: no credentials (the URL is presigned).
      const res = await fetchImpl(url, { method: "PUT", body, signal });
      if (!res.ok) {
        throw new ApiError(res.status, `That part failed to upload (${res.status}).`);
      }
      const etag = res.headers.get("etag") ?? res.headers.get("ETag");
      if (!etag) {
        // The bucket must expose ETag via CORS; without it we can't complete.
        throw new ApiError(0, "R2 didn't return an ETag for that part.");
      }
      return { part_number: partNumber, etag };
    } catch (err) {
      lastErr = err;
      if (signal?.aborted) throw new ApiError(0, "Upload canceled.");
    }
  }
  throw lastErr instanceof Error
    ? new ApiError(0, `We couldn't upload part ${partNumber}. ${lastErr.message}`)
    : new ApiError(0, `We couldn't upload part ${partNumber}.`);
}

/**
 * One part sent on its own, with the engine's retry rules: backed off, and each retry on a fresh signature.
 * Why it exists: a take that goes up while it is still recorded has its parts one at a time, before the whole
 * file is there for `uploadParts`.
 */
export async function uploadOnePart<T>(
  calls: PartUploadCalls<T>,
  partNumber: number,
  body: Blob,
  options: Pick<ResumableUploadOptions, "fetchImpl" | "maxRetriesPerPart" | "delayImpl" | "signal"> = {},
): Promise<UploadPartResult> {
  const urlFor = async (): Promise<string> => {
    const url = (await signParts(calls, [partNumber], options.signal)).get(partNumber);
    if (!url) throw new ApiError(0, `Missing a signed URL for part ${partNumber}.`);
    return url;
  };
  return putPartDirect(
    options.fetchImpl ?? fetch,
    urlFor,
    partNumber,
    body,
    options.maxRetriesPerPart ?? 5,
    options.delayImpl ?? ((ms: number) => wait(ms, options.signal)),
    options.signal,
  );
}

/** How many parts one sign request covers. Small on purpose: URLs live 15
 * minutes, so a slow connection should only ever hold a batch it can finish. */
const SIGN_BATCH = 16;

/**
 * Upload `file` to R2 in resumable parts of `partSize` (presigned, direct-to-R2),
 * then complete through `calls`. Re-run with `resumeFrom` set to the receipts you
 * already have to resume: only the missing parts are re-signed and re-sent.
 */
async function uploadParts<T>(
  file: Blob,
  partSize: number,
  calls: PartUploadCalls<T>,
  options: ResumableUploadOptions = {},
): Promise<T> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const maxRetries = options.maxRetriesPerPart ?? 5;
  const delay = options.delayImpl ?? ((ms: number) => wait(ms, options.signal));
  const plan = planParts(file.size, partSize);
  const done = new Map<number, UploadPartResult>(
    (options.resumeFrom ?? []).map((p) => [p.part_number, p]),
  );

  // Signing is LAZY, a batch at a time as the pool advances: URLs live 15
  // minutes, and signing 700 parts up front handed a slow connection hundreds
  // of URLs that were dead long before their turn came.
  const urls = new Map<number, string>();

  // Receipts live in a fixed slot per part number, so /complete gets them in
  // part-number order no matter which PUT finished first. Resumed parts fill
  // their slot up front and are never re-sent or re-reported.
  const slots: Array<UploadPartResult | undefined> = Array.from({ length: plan.length });
  for (const receipt of done.values()) slots[receipt.part_number - 1] = receipt;

  // The missing parts go through a small pool: at most `partsInFlight` PUTs in
  // flight, parts claimed in ascending order. A failure (a part past its
  // retries, or the abort signal) stops new dispatch; the parts already flying
  // are allowed to land, and the first failure is rethrown. Why the reported
  // count starts at done.size: only NEWLY finished parts fire onPartComplete
  // (resumed ones are not reported again), but the caller's progress bar counts
  // every part of the whole upload.
  const missingParts = plan.filter((p) => !done.has(p.partNumber));
  const limit = Math.max(1, Math.min(Math.floor(options.partsInFlight ?? 6) || 1, missingParts.length || 1));

  // One sign request in flight at a time, shared by every worker; each covers
  // the next SIGN_BATCH unsigned parts in order.
  let signedUpTo = 0;
  let signFlight: Promise<void> | null = null;
  const urlOf = async (partNumber: number): Promise<string> => {
    while (!urls.has(partNumber)) {
      if (!signFlight) {
        const batch = missingParts
          .slice(signedUpTo, signedUpTo + SIGN_BATCH)
          .map((p) => p.partNumber);
        signedUpTo += SIGN_BATCH;
        signFlight = signParts(calls, batch, options.signal)
          .then((m) => {
            for (const [k, v] of m) urls.set(k, v);
          })
          .finally(() => {
            signFlight = null;
          });
      }
      await signFlight;
    }
    return urls.get(partNumber) as string;
  };
  // A retry never trusts the URL it failed on: one fresh signature, one part.
  const resign = async (partNumber: number): Promise<string> => {
    const m = await signParts(calls, [partNumber], options.signal);
    const url = m.get(partNumber);
    if (!url) throw new ApiError(0, `Missing a signed URL for part ${partNumber}.`);
    urls.set(partNumber, url);
    return url;
  };

  let next = 0;
  let finished = 0;
  let failure: unknown = null;
  const worker = async (): Promise<void> => {
    while (failure === null && next < missingParts.length) {
      // A canceled upload starts no more parts; the PUTs already in flight are
      // cut off by the signal they carry.
      if (options.signal?.aborted) {
        failure = failure ?? new ApiError(0, "Upload canceled.");
        return;
      }
      const p = missingParts[next++];
      try {
        const receipt = await putPartDirect(
          fetchImpl,
          (fresh) => (fresh ? resign(p.partNumber) : urlOf(p.partNumber)),
          p.partNumber,
          file.slice(p.start, p.end),
          maxRetries,
          delay,
          options.signal,
        );
        slots[p.partNumber - 1] = receipt;
        finished += 1;
        options.onPartComplete?.(receipt, done.size + finished, plan.length);
      } catch (err) {
        failure = failure ?? err;
        return;
      }
    }
  };
  await Promise.all(Array.from({ length: limit }, () => worker()));
  if (failure) throw failure;

  const receipts: UploadPartResult[] = [];
  for (let i = 0; i < plan.length; i++) {
    const receipt = slots[i];
    if (!receipt) throw new ApiError(0, `Missing a receipt for part ${i + 1}.`);
    receipts.push(receipt);
  }

  // Every part is in. From here the multipart is the server's to finish.
  options.onBeforeComplete?.();
  // Completing a canceled upload would assemble the very object the caller is
  // about to abort, so this is the last place the signal is checked.
  if (options.signal?.aborted) throw new ApiError(0, "Upload canceled.");
  return calls.complete(receipts, options.signal);
}

/**
 * The webinar recording through the part engine. Returns the complete response
 * (`state:"probing"`).
 */
export function uploadResumableToR2(
  webinarId: string,
  file: Blob,
  start: R2UploadStartResponse,
  options: ResumableUploadOptions = {},
): Promise<UploadCompleteResponse> {
  const calls = webinarCalls(options.fetchImpl ?? fetch, webinarId, start);
  return uploadParts(file, start.part_size, calls, options);
}

/**
 * The upload that survives a bad connection: run the resumable upload, and when
 * it fails mid-way, pick it straight back up from the receipts already earned.
 * As long as each round makes progress the retries are free; only consecutive
 * rounds that move NOTHING count against `recoveryAttempts`.
 */
export async function uploadPartsWithRecovery<T>(
  file: Blob,
  partSize: number,
  calls: PartUploadCalls<T>,
  options: ResumableUploadOptions & { recoveryAttempts?: number } = {},
): Promise<T> {
  const receipts = new Map<number, UploadPartResult>(
    (options.resumeFrom ?? []).map((p) => [p.part_number, p]),
  );
  const attempts = Math.max(1, options.recoveryAttempts ?? 3);
  const delay = options.delayImpl ?? ((ms: number) => wait(ms, options.signal));
  let stale = 0;
  for (;;) {
    const before = receipts.size;
    try {
      return await uploadParts(file, partSize, calls, {
        ...options,
        resumeFrom: [...receipts.values()],
        onPartComplete: (part, done, total) => {
          receipts.set(part.part_number, part);
          options.onPartComplete?.(part, done, total);
        },
      });
    } catch (err) {
      if (options.signal?.aborted) throw err;
      stale = receipts.size > before ? 0 : stale + 1;
      if (stale >= attempts) throw err;
      await delay(2000 * stale);
    }
  }
}

/**
 * The webinar recording, recovering mid-way. The failure that finally surfaces
 * says the retry will resume, because it will: the receipts ride
 * `onPartComplete` into the caller's `resumeFrom`.
 */
export async function uploadWithRecovery(
  webinarId: string,
  file: Blob,
  start: R2UploadStartResponse,
  options: ResumableUploadOptions & { recoveryAttempts?: number } = {},
): Promise<UploadCompleteResponse> {
  let kept = (options.resumeFrom ?? []).length > 0;
  const calls = webinarCalls(options.fetchImpl ?? fetch, webinarId, start);
  try {
    return await uploadPartsWithRecovery(file, start.part_size, calls, {
      ...options,
      onPartComplete: (part, done, total) => {
        kept = true;
        options.onPartComplete?.(part, done, total);
      },
    });
  } catch (err) {
    if (!options.signal?.aborted && kept && err instanceof ApiError) {
      throw new ApiError(err.status, `${err.message} Trying again picks up where it stopped.`);
    }
    throw err;
  }
}

/**
 * How far one upload has got. `finishing` starts the moment the last part is
 * acknowledged, which is when the browser asks the server to assemble them.
 */
export type UploadPhase = "idle" | "reserving" | "uploading" | "finishing" | "settled";

/**
 * Is the multipart still ours to cancel? Only until the parts are assembled: the
 * complete step flips the webinar to `probing` and queues the transcode, and
 * aborting after that deletes the job row the transcode runs against, leaving the
 * webinar stuck at `probing` and unpublishable for good. An abandoned multipart is
 * the smaller loss, so a complete that FAILED is not abortable either: the browser
 * cannot tell whether the server took it.
 */
export function canAbortUpload(phase: UploadPhase): boolean {
  return phase === "reserving" || phase === "uploading";
}

/** The abort call a departing view owes the server, if any. */
export function pendingUploadAbort(
  phase: UploadPhase,
  webinarId: string | null,
  reserved: UploadAbortRequest | null,
): { id: string; body: UploadAbortRequest } | null {
  if (!webinarId || !reserved || !canAbortUpload(phase)) return null;
  return { id: webinarId, body: reserved };
}

